import { useState } from "react";
import { Send, MessageCircle, Link2, QrCode, Check, X, Share2 } from "lucide-react";
import { toast } from "sonner";

/** Reusable social share bar: WhatsApp, Facebook, LinkedIn, Telegram, WeChat (QR) + copy link. */
export default function ShareBar({ url, title = "My Kind", text = "", className = "" }) {
  const [copied, setCopied] = useState(false);
  const [wechat, setWechat] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "https://mykind.app");
  const msg = `${text ? text + " " : ""}${shareUrl}`;
  const e = encodeURIComponent;

  const targets = [
    { key: "whatsapp", label: "WhatsApp", color: "#25D366", href: `https://wa.me/?text=${e(msg)}`, Icon: MessageCircle },
    { key: "facebook", label: "Facebook", color: "#1877F2", href: `https://www.facebook.com/sharer/sharer.php?u=${e(shareUrl)}`, initials: "f" },
    { key: "linkedin", label: "LinkedIn", color: "#0A66C2", href: `https://www.linkedin.com/sharing/share-offsite/?url=${e(shareUrl)}`, initials: "in" },
    { key: "telegram", label: "Telegram", color: "#229ED9", href: `https://t.me/share/url?url=${e(shareUrl)}&text=${e(text)}`, Icon: Send },
    { key: "wechat", label: "WeChat", color: "#07C160", Icon: QrCode, action: () => setWechat(true) },
  ];

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1600); }
    catch { toast.error("Couldn't copy"); }
  };
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title, text, url: shareUrl }); } catch { /* cancelled */ } }
    else copy();
  };
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${e(shareUrl)}`;

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="share-bar">
      {targets.map((t) => {
        const inner = (
          <>
            {t.Icon ? <t.Icon size={16} /> : <span className="w-4 text-center font-bold text-[13px] leading-none">{t.initials}</span>}
            <span className="hidden sm:inline">{t.label}</span>
          </>
        );
        const cls = "inline-flex items-center gap-2 rounded-full px-3.5 h-9 text-sm font-medium text-white transition-transform hover:-translate-y-0.5";
        return t.action ? (
          <button key={t.key} data-testid={`share-${t.key}`} onClick={t.action} className={cls} style={{ backgroundColor: t.color }}>{inner}</button>
        ) : (
          <a key={t.key} data-testid={`share-${t.key}`} href={t.href} target="_blank" rel="noopener noreferrer" className={cls} style={{ backgroundColor: t.color }}>{inner}</a>
        );
      })}
      <button data-testid="share-native" onClick={nativeShare} className="inline-flex items-center gap-2 rounded-full px-3.5 h-9 text-sm rud-card hover:border-brand/60 transition-colors"><Share2 size={16} /> <span className="hidden sm:inline">More</span></button>
      <button data-testid="share-copy" onClick={copy} className="inline-flex items-center gap-2 rounded-full px-3.5 h-9 text-sm rud-card hover:border-brand/60 transition-colors">{copied ? <Check size={16} className="text-emerald-400" /> : <Link2 size={16} />} <span className="hidden sm:inline">{copied ? "Copied" : "Copy link"}</span></button>

      {wechat && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setWechat(false)} data-testid="wechat-modal">
          <div className="rud-card rounded-2xl p-6 max-w-xs w-full text-center relative" onClick={(ev) => ev.stopPropagation()}>
            <button onClick={() => setWechat(false)} className="absolute top-3 right-3 text-sec hover:text-brand" data-testid="wechat-close"><X size={18} /></button>
            <div className="text-sm font-medium">Share on WeChat</div>
            <p className="text-xs text-sec mt-1 mb-4">Open WeChat → Discover → Scan, and scan this code.</p>
            <img src={qrSrc} alt="WeChat QR" className="mx-auto rounded-lg bg-white p-2" width={220} height={220} />
          </div>
        </div>
      )}
    </div>
  );
}
